import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import {
  LOCATION_SORT_FIELDS,
  LOCATION_STATUS_LABEL_HU,
  LOCATION_TYPE_LABEL_HU,
  LocationStatus,
  LocationType,
  type LocationSortField,
} from '@persped/shared';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLocations } from '../../api/queries';
import { PrintRangeDialog } from './PrintRangeDialog';
import { ProblemAlert } from '../../components/ProblemAlert';
import { NewLocationDialog } from './NewLocationDialog';

/**
 * Lokációk listája (spec 23. képernyő, US-LOCATION-1).
 *
 * A `GET /locations` végpont NEM lapoz — a teljes listát adja. Ez ma
 * elfogadható (~13 lokáció), de ha a szám érdemben nő, a lapozást a
 * szerveroldalon kell bevezetni, nem itt elrejteni.
 */
/**
 * Lokációlista — és ugyanez a komponens szolgálja a RAKLAPOK oldalt is
 * (D fázis, 8.1 pont).
 *
 * MIÉRT EGY KOMPONENS: mindhárom lokációtípusnál azonos a lista, a szűrés és
 * az adatlapra navigálás. A `fixedType` prop MEGSZŰNT (2026-08-14-i CR #15):
 * a „Raklapok" külön menüpont sem kell, mert a raklap a három egyenrangú
 * lokációtípus egyike. A régi `/raklapok` útvonal ide irányít át
 * `?tipus=PALLET`-tel, hogy az elmentett linkek a várt szűrt listát adják.
 */
const SORT_LABEL_HU: Record<LocationSortField, string> = {
  barcode: 'Vonalkód',
  name: 'Név',
  locationType: 'Típus',
  createdAt: 'Létrehozva',
};

export function LocationListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A kezdőérték az URL-ből: így a `/raklapok` átirányítás a raklapokra szűrt
  // listát adja, és a link megosztható marad.
  const [locationType, setLocationType] = useState(searchParams.get('tipus') ?? '');
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<LocationSortField>('barcode');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [createOpen, setCreateOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);

  const locations = useLocations({
    locationType: locationType || undefined,
    status: status || undefined,
    query: query.trim() || undefined,
    sortBy,
    sortDir,
  });

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
          Lokációk
        </Typography>
        {/*
          Címketartomány nyomtatása (2026-08-14-i CR #23) — ez LÉTREHOZZA a
          hiányzó lokációkat. A fizikai munkafolyamat ezt kívánja: ötven üres
          raklap áll a raktárban, és a dolgozó felragasztja rájuk a címkéket.
        */}
        <Button variant="outlined" onClick={() => setRangeOpen(true)}>
          Címketartomány nyomtatása
        </Button>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreateOpen(true)}>
          Új lokáció
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          <TextField
            label="Keresés (vonalkód vagy név)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ minWidth: 260 }}
          />
          <TextField
            select
            label="Típus"
            value={locationType}
            onChange={(e) => setLocationType(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Mind</MenuItem>
            {Object.values(LocationType).map((t) => (
              <MenuItem key={t} value={t}>
                {LOCATION_TYPE_LABEL_HU[t]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Státusz"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Mind</MenuItem>
            {Object.values(LocationStatus).map((s) => (
              <MenuItem key={s} value={s}>
                {LOCATION_STATUS_LABEL_HU[s]}
              </MenuItem>
            ))}
          </TextField>
          {/* Rendezés (08-13 CR 8.1) — a Mappák oldal mintájára. */}
          <TextField
            select
            label="Rendezés"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as LocationSortField)}
            sx={{ minWidth: 180 }}
          >
            {LOCATION_SORT_FIELDS.map((f) => (
              <MenuItem key={f} value={f}>
                {SORT_LABEL_HU[f]}
              </MenuItem>
            ))}
          </TextField>
          <Button onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}>
            {sortDir === 'asc' ? 'Növekvő' : 'Csökkenő'}
          </Button>
        </Stack>
      </Paper>

      {locations.isError && <ProblemAlert error={locations.error} />}

      {locations.isPending ? (
        <Stack sx={{ py: 6, alignItems: 'center' }}>
          <CircularProgress />
        </Stack>
      ) : locations.data ? (
        <Paper variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Vonalkód</TableCell>
                  <TableCell>Név</TableCell>
                  <TableCell>Típus</TableCell>
                  <TableCell>Státusz</TableCell>
                  <TableCell align="right">Mappák</TableCell>
                  <TableCell align="right">Szintek</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        Nincs a szűrésnek megfelelő lokáció.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {locations.data.map((loc) => (
                  <TableRow
                    key={loc.barcode}
                    hover
                    sx={{ cursor: 'pointer', opacity: loc.status === 'INACTIVE' ? 0.55 : 1 }}
                    onClick={() => void navigate(`/lokaciok/${loc.barcode}`)}
                  >
                    <TableCell sx={{ fontFamily: 'monospace' }}>{loc.barcode}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        {loc.name}
                        {loc.isDefaultStaging && (
                          <Chip size="small" label="Alapértelmezett" variant="outlined" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{LOCATION_TYPE_LABEL_HU[loc.locationType]}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={loc.status === 'ACTIVE' ? 'success' : 'default'}
                        label={LOCATION_STATUS_LABEL_HU[loc.status]}
                      />
                    </TableCell>
                    <TableCell align="right">{loc.folderCount}</TableCell>
                    <TableCell align="right">{loc.levels ? loc.levels.length : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}

      <NewLocationDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <PrintRangeDialog open={rangeOpen} onClose={() => setRangeOpen(false)} />
    </Stack>
  );
}
